import API from '@/APIs/API';
import {RootStore} from '@/Core/mobx/RootStore';
import {makeAutoObservable, toJS} from 'mobx';
import {extractCurrentCallUserData, extractParameterStoreData} from './helpers/extractRelevantUserData';

export class AssistStore {
    sessionId: string = '';
    queryHistory: {query: string; response: string; responseId: string; wasHelpful: boolean | null}[] = [];
    isCreatingSession: boolean = false;
    isSendingQuery: boolean = false;
    isPolling: boolean = false;
    notes: string = '';
    initialResponse: {response: string; responseId: string; wasHelpful: boolean | null} = {
        response: '',
        responseId: '',
        wasHelpful: null,
    };

    constructor(private parentStore: RootStore) {
        makeAutoObservable(this);
    }

    resetStore = () => {
        this.sessionId = '';
        this.queryHistory = [];
        this.isCreatingSession = false;
        this.isSendingQuery = false;
        this.isPolling = false;
        this.notes = '';
        this.initialResponse = {response: '', responseId: '', wasHelpful: null};
    };

    startAssist = async ({shouldAllowResume, isLiveCall}: {shouldAllowResume: boolean; isLiveCall: boolean}) => {
        this.isCreatingSession = true;
        try {
            if (shouldAllowResume) {
                try {
                    const resumeResponse = await this.attemptResumeAssist(isLiveCall);
                    if (resumeResponse) {
                        this.isCreatingSession = false;
                        return;
                    }
                } catch (error) {
                    console.error('Unable to resume assist', error);
                    // Continue with new session
                }
            }

            const response = await API.Call<any>('v2/ai-chat/session', 'POST', {
                userId: isLiveCall
                    ? this.parentStore.liveTabStore.currentCall?.clientEmail
                    : this.parentStore.agentPortalStore.contactsFormStore.contact,
                userData: {
                    ...extractCurrentCallUserData(toJS(this.parentStore.liveTabStore.currentCall)),
                    // notes: this.notes,
                    ...extractParameterStoreData(toJS(this.parentStore.agentPortalStore.parameterStore)),
                },
            });
            if (response) {
                this.sessionId = response.sessionId;

                // If responseId is returned, poll for the response and set it as initial response
                if (response.responseId) {
                    try {
                        const aiResponse = await this.pollResponseStatus(response.responseId);
                        this.initialResponse = aiResponse;
                        console.log('Initial response:', this.initialResponse);
                    } catch (error) {
                        console.error('Error polling initial response:', error);
                        this.initialResponse = {
                            response:
                                'Sorry, I encountered an error while getting the initial response. Please try again.',
                            responseId: '',
                            wasHelpful: null,
                        };
                    }
                }
            }
        } catch (error) {
            console.error('Error starting assist:', error);
        } finally {
            this.isCreatingSession = false;
        }
    };

    private pollResponseStatus = async (
        responseId: string,
    ): Promise<{response: string; responseId: string; wasHelpful: boolean | null}> => {
        this.isPolling = true;
        const maxAttempts = 30;
        let attempts = 0;

        try {
            while (true) {
                if (attempts >= maxAttempts) {
                    throw new Error('Polling timeout: AI response took too long');
                }
                attempts++;

                const response = await API.Call<any>(
                    `v2/ai-chat/session/${this.sessionId}/response/${responseId}`,
                    'GET',
                );

                if (response?.status) {
                    if (response.status === 'completed') {
                        return {
                            response: response.response || '',
                            responseId: response.responseId || '',
                            wasHelpful: response.wasHelpful || null,
                        };
                    } else if (response.status === 'failed') {
                        throw new Error('AI response failed');
                    }
                }

                // Wait 2 second before next poll
                await new Promise((resolve) => setTimeout(resolve, 2000));
            }
        } catch (error) {
            console.error('Error polling response status:', error);
            throw error;
        } finally {
            this.isPolling = false;
        }
    };

    sendQuery = async (message: string) => {
        this.isSendingQuery = true;
        try {
            const response = await API.Call<any>(`v2/ai-chat/session/${this.sessionId}/chat`, 'POST', {
                message,
            });
            if (response) {
                const {responseId, status} = response;
                let aiResponse = '';

                if (status === 'processing') {
                    aiResponse = (await this.pollResponseStatus(responseId)).response;
                } else if (status === 'completed') {
                    aiResponse = response.response;
                } else if (status === 'failed') {
                    throw new Error('AI response failed');
                }
                this.queryHistory.push({
                    query: message,
                    response: aiResponse,
                    responseId: responseId,
                    wasHelpful: null,
                });
            }
        } catch (error) {
            console.error('Error sending query:', error);
            // Add error message to history
            this.queryHistory.push({
                query: message,
                response: 'Sorry, I encountered an error. Please try again.',
                responseId: '',
                wasHelpful: null,
            });
        } finally {
            this.isSendingQuery = false;
        }
    };

    setNotes = (notes: string) => {
        this.notes = notes;

        // Update userData if sessionId is not empty
        /*
        if (this.sessionId !== '') {
            this.handleNotesUpdateForSession(notes);
        }
        */
    };

    private handleNotesUpdateForSession = async (notes: string) => {
        try {
            await API.Call<any>(`v2/ai-chat/session/${this.sessionId}/user-data`, 'POST', {
                userNotes: notes,
            });
        } catch (error) {
            console.error('Error updating notes for session:', error);
        }
    };

    handleFeedbackChange = (responseId: string, wasHelpful: boolean) => {
        const sendRequest = <T extends {responseId: string; wasHelpful: boolean | null}>(item: T): T => {
            const oldState = item.wasHelpful;
            let newState = oldState === wasHelpful ? null : wasHelpful;
            API.Call<any>(`v2/ai-chat/response/${item.responseId}/sentiment`, 'POST', {
                wasHelpful: newState,
            });
            return {...item, wasHelpful: newState};
        };
        if (this.initialResponse.responseId === responseId) {
            this.initialResponse = sendRequest(this.initialResponse);
            return;
        }
        this.queryHistory = this.queryHistory.map((item) => {
            if (item.responseId === responseId) {
                return sendRequest(item);
            }
            return item;
        });
    };

    private attemptResumeAssist = async (isLiveCall: boolean): Promise<boolean> => {
        try {
            const resumeResponse = await API.Call<any>('v2/ai-chat/resume', 'POST', {
                userId: isLiveCall
                    ? this.parentStore.liveTabStore.currentCall?.clientEmail
                    : this.parentStore.agentPortalStore.contactsFormStore.contact,
            });
            if (resumeResponse) {
                this.sessionId = resumeResponse.sessionId;
                this.initialResponse = resumeResponse.responses?.[0];
                this.queryHistory = resumeResponse.responses?.slice(1).map((response: any) => ({
                    query: response.message,
                    response: response.response,
                    responseId: response.responseId,
                    wasHelpful: response.wasHelpful,
                }));
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error resuming assist:', error);
            return false;
        }
    };

    resetSession = async (shouldAllowResume: boolean = false, isLiveCall: boolean = false) => {
        this.resetStore();
        await this.startAssist({shouldAllowResume, isLiveCall});
    };
}
